
/*
  UXS — ALIEN ORGANISM V4.2
  DAY2K / UNIDOS X EL SONIDO

  Conserva V4.1:
  - Micrófono
  - VOICE001 y VOICE002
  - Audio de señales entrantes
  - Contorno orgánico original
  - Picos reactivos al sonido

  Nuevo:
  - Implosión geométrica
  - Pliegues hacia adentro
  - Vibración y tensión
  - Liberación suave

  La nueva animación solo aparece
  durante la transición entrante.
*/

(function () {
  function initialize() {
    const canvas = document.getElementById(
      "uxs-visualizer"
    );

    if (!canvas || canvas.dataset.ready) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    canvas.dataset.ready = "true";

    const AudioContextClass =
      window.AudioContext || window.webkitAudioContext;

    // CONFIGURACIÓN ORIGINAL
    const config = {
      radius: 76,
      outward: 72,
      inward: 43,
      sensitivity: 13,
      spikeCount: 34,
      attack: 0.60,
      release: 0.16,
      chaos: 1.3,
      strokeWidth: 1.65,
      points: 512
    };

    const N = config.points;
    const contour = new Float32Array(N);
    const target = new Float32Array(N);

    const players = new WeakMap();
    const audioBuffers = new WeakMap();
    const spikes = [];

    const PLAYER_IDS = new Set([
      "uxs-vocoder-player",
      "uxs-voice001-player",
      "uxs-incoming-audio",
      "uxs-incoming-voice001-player"
    ]);

    let audioContext = null;

    let micSource = null;
    let micAnalyser = null;
    let micActive = false;
    let activePlayer = null;

    let energy = 0;
    let previousEnergy = 0;

    let lastSpawn = 0;
    let lastFrame = 0;
    let size = 320;

    // Solo para la nueva transición
    let morphIntensity = 0;

    const random = (a, b) =>
      a + Math.random() * (b - a);

    const clamp = (x, a, b) =>
      Math.min(b, Math.max(a, x));

    // ========================================
    // CANVAS
    // ========================================

    function resize() {
      size =
        canvas.getBoundingClientRect().width || 320;

      const dpr = Math.min(
        window.devicePixelRatio || 1,
        2
      );

      canvas.width = Math.round(size * dpr);
      canvas.height = Math.round(size * dpr);

      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    }

    resize();
    window.addEventListener("resize", resize);

    // ========================================
    // AUDIO CONTEXT
    // ========================================

    async function getContext() {
      if (!AudioContextClass) {
        throw new Error("Web Audio no disponible");
      }

      if (!audioContext) {
        audioContext = new AudioContextClass();
      }

      if (audioContext.state === "suspended") {
        await audioContext.resume();
      }

      return audioContext;
    }

    function makeAnalyser(context) {
      const analyser = context.createAnalyser();

      analyser.fftSize = 2048;
      analyser.smoothingTimeConstant = 0.46;

      audioBuffers.set(analyser, {
        time: new Uint8Array(analyser.fftSize),
        freq: new Uint8Array(
          analyser.frequencyBinCount
        )
      });

      return analyser;
    }

    // ========================================
    // MICRÓFONO ORIGINAL
    // ========================================

    async function connectMicrophone(stream) {
      const context = await getContext();

      disconnectMicrophone();

      micSource =
        context.createMediaStreamSource(stream);

      micAnalyser = makeAnalyser(context);

      micSource.connect(micAnalyser);

      // No conectar a los altavoces:
      // evita escuchar el micrófono con eco.
      micActive = true;
    }

    function disconnectMicrophone() {
      micActive = false;

      if (micSource) {
        micSource.disconnect();
        micSource = null;
      }

      if (micAnalyser) {
        micAnalyser.disconnect();
        micAnalyser = null;
      }
    }

    window.UXSVisualizer = {
      connectMicrophone,
      disconnectMicrophone
    };

    // ========================================
    // REPRODUCTORES
    // ========================================

    async function connectPlayer(player) {
      const context = await getContext();

      let connection = players.get(player);

      if (!connection) {
        const source =
          context.createMediaElementSource(player);

        const analyser = makeAnalyser(context);

        source.connect(analyser);
        analyser.connect(context.destination);

        connection = {
          source,
          analyser
        };

        players.set(player, connection);
      }

      activePlayer = player;
    }

    document.addEventListener(
      "play",
      event => {
        const player = event.target;

        if (
          !player ||
          !PLAYER_IDS.has(player.id)
        ) {
          return;
        }

        connectPlayer(player).catch(error => {
          console.error("UXS Visualizer:", error);
        });
      },
      true
    );

    ["pause", "ended"].forEach(eventName => {
      document.addEventListener(
        eventName,
        event => {
          const player = event.target;

          if (
            !player ||
            !PLAYER_IDS.has(player.id)
          ) {
            return;
          }

          if (activePlayer === player) {
            activePlayer = null;
          }
        },
        true
      );
    });

    function activeAnalyser() {
      if (micActive && micAnalyser) {
        return micAnalyser;
      }

      if (
        activePlayer &&
        !activePlayer.paused
      ) {
        const connection =
          players.get(activePlayer);

        if (connection) {
          return connection.analyser;
        }
      }

      return null;
    }

    // ========================================
    // REACTIVIDAD ORIGINAL
    // ========================================

    function readAudio() {
      const analyser = activeAnalyser();

      if (!analyser) {
        energy += (0 - energy) * 0.20;
        previousEnergy = energy;

        return {
          level: energy,
          onset: 0,
          texture: 0
        };
      }

      const buffers = audioBuffers.get(analyser);

      if (!buffers) {
        return {
          level: 0,
          onset: 0,
          texture: 0
        };
      }

      analyser.getByteTimeDomainData(
        buffers.time
      );

      analyser.getByteFrequencyData(
        buffers.freq
      );

      let sum = 0;

      for (
        let i = 0;
        i < buffers.time.length;
        i++
      ) {
        const x =
          (buffers.time[i] - 128) / 128;

        sum += x * x;
      }

      const rms = Math.sqrt(
        sum / buffers.time.length
      );

      const level = clamp(
        Math.pow(
          rms * config.sensitivity,
          0.72
        ),
        0,
        1
      );

      const onset = Math.max(
        0,
        level - previousEnergy
      );

      previousEnergy = level;

      const speed =
        level > energy ? 0.53 : 0.20;

      energy += (level - energy) * speed;

      let high = 0;
      let total = 0;

      const start = Math.floor(
        buffers.freq.length * 0.09
      );

      const end = Math.floor(
        buffers.freq.length * 0.38
      );

      for (
        let i = start;
        i < end;
        i++
      ) {
        high += buffers.freq[i];
        total++;
      }

      const texture = total
        ? clamp(high / total / 170, 0, 1)
        : 0;

      return {
        level: energy,
        onset,
        texture
      };
    }

    // ========================================
    // PICOS ORGÁNICOS ORIGINALES
    // ========================================

    function createSpike(intensity, texture) {
      const inward = Math.random() < 0.35;

      spikes.push({
        angle: random(0, Math.PI * 2),
        width: random(0.018, 0.115),

        amplitude:
          (
            inward
              ? -config.inward
              : config.outward
          ) *
          random(0.25, 1.0) *
          (0.5 + intensity * 0.8),

        life: 0,
        duration: random(0.12, 0.55),
        drift: random(-1.5, 1.5),
        narrowness: random(0.7, 2.6),
        texture
      });

      if (spikes.length > config.spikeCount) {
        spikes.shift();
      }
    }

    function updateSpikes(dt, audio, now) {
      const { level, onset, texture } = audio;

      const interval = 110 - level * 85;

      if (
        level > 0.035 &&
        now - lastSpawn > interval
      ) {
        const amount =
          1 +
          (onset > 0.08 ? 3 : 0) +
          (level > 0.55 ? 2 : 0);

        for (let i = 0; i < amount; i++) {
          createSpike(level, texture);
        }

        lastSpawn = now;
      }

      for (
        let i = spikes.length - 1;
        i >= 0;
        i--
      ) {
        const spike = spikes[i];

        spike.life += dt;
        spike.angle += spike.drift * dt;

        if (spike.life >= spike.duration) {
          spikes.splice(i, 1);
        }
      }

      target.fill(0);

      for (const spike of spikes) {
        const progress =
          spike.life / spike.duration;

        const envelope =
          (1 - Math.exp(-progress * 24)) *
          Math.pow(1 - progress, 1.3);

        for (let i = 0; i < N; i++) {
          const angle =
            i / N * Math.PI * 2;

          let distance = Math.abs(
            angle - spike.angle
          );

          distance = Math.min(
            distance,
            Math.PI * 2 - distance
          );

          const normalized =
            distance / spike.width;

          const body = Math.exp(
            -Math.pow(
              normalized,
              spike.narrowness
            )
          );

          const needle = Math.exp(
            -Math.pow(
              normalized * 3.5,
              1.3
            )
          );

          target[i] +=
            spike.amplitude *
            envelope *
            (
              body * 0.55 +
              needle * 0.45
            );
        }
      }

      const detailAmount =
        level *
        (4 + texture * 13) *
        config.chaos;

      for (let i = 0; i < N; i++) {
        const a =
          i / N * Math.PI * 2;

        const fine =
          Math.sin(
            a * 43 + now * 0.0027
          ) *
          Math.sin(
            a * 89 - now * 0.0016
          ) *
          Math.cos(
            a * 17 + now * 0.0011
          );

        target[i] += fine * detailAmount;

        target[i] = clamp(
          target[i],
          -config.inward,
          config.outward
        );

        const rate =
          target[i] > contour[i]
            ? config.attack
            : config.release;

        const factor =
          1 - Math.pow(
            1 - rate,
            dt * 60
          );

        contour[i] +=
          (target[i] - contour[i]) *
          factor;
      }
    }

    // ========================================
    // NUEVO: LEER CONTRACCIÓN EXISTENTE
    // ========================================

    function readIncomingScale() {
      const transform =
        getComputedStyle(canvas).transform;

      if (
        !transform ||
        transform === "none"
      ) {
        return 1;
      }

      const match2d =
        /^matrix\(([^)]+)\)/.exec(transform);

      if (match2d) {
        const values = match2d[1]
          .split(",")
          .map(Number);

        return clamp(
          Math.hypot(values[0], values[1]),
          0.05,
          1.5
        );
      }

      const match3d =
        /^matrix3d\(([^)]+)\)/.exec(transform);

      if (match3d) {
        const values = match3d[1]
          .split(",")
          .map(Number);

        return clamp(
          Math.hypot(
            values[0],
            values[1],
            values[2]
          ),
          0.05,
          1.5
        );
      }

      return 1;
    }

    // ========================================
    // DIBUJAR ORGANISMO
    // ========================================

    function draw() {
      ctx.clearRect(0, 0, size, size);

      const center = size / 2;
      const scale = size / 320;
      const base = config.radius * scale;

      const root = document.getElementById(
        "uxs-experience"
      );

      const transitioning =
        root?.classList.contains(
          "uxs-incoming-transitioning"
        );

      // El archivo de transición continúa
      // controlando el tamaño del canvas.
      const incomingScale = transitioning
        ? readIncomingScale()
        : 1;

      const desired = transitioning
        ? clamp(
            (1 - incomingScale) / 0.87,
            0,
            1
          )
        : 0;

      // La tensión sube rápidamente,
      // pero desaparece con suavidad.
      morphIntensity +=
        (desired - morphIntensity) *
        (
          desired > morphIntensity
            ? 0.22
            : 0.12
        );

      if (morphIntensity < 0.0001) {
        morphIntensity = 0;
      }

      const t = performance.now() * 0.001;

      const tension = Math.pow(
        morphIntensity,
        0.8
      );

      ctx.beginPath();

      for (let i = 0; i <= N; i++) {
        const k = i % N;

        const a =
          k / N * Math.PI * 2 -
          Math.PI / 2;

        // Ondas que se retuercen
        // en direcciones opuestas.
        const turbulent =
          (
            Math.sin(
              a * 11 + t * 15
            ) * 0.47 +

            Math.sin(
              a * 23 - t * 24
            ) * 0.34 +

            Math.cos(
              a * 37 + t * 29
            ) * 0.19
          ) * 30 * tension;

        // Pliegues que se hunden
        // hacia el centro.
        const folds =
          Math.pow(
            Math.max(
              0,
              Math.sin(
                a * 7 - t * 11
              )
            ),
            3
          ) * 39 * tension;

        // Picos finos que aparecen
        // mientras el ente lucha
        // contra la implosión.
        const needles =
          Math.pow(
            Math.max(
              0,
              Math.sin(
                a * 17 + t * 19
              )
            ),
            8
          ) * 21 * tension;

        // Latido del núcleo.
        const pulse =
          Math.sin(
            t * (
              8 +
              morphIntensity * 14
            )
          ) * 5 * tension;

        // Conservamos el contorno
        // original reactivo al audio.
        const r = Math.max(
          7 * scale,

          base +
          contour[k] * scale +

          (
            turbulent -
            folds +
            needles +
            pulse
          ) * scale
        );

        const x =
          center +
          Math.cos(a) * r;

        const y =
          center +
          Math.sin(a) * r;

        if (i === 0) {
          ctx.moveTo(x, y);
        } else {
          ctx.lineTo(x, y);
        }
      }

      ctx.closePath();

      ctx.fillStyle = "#050505";
      ctx.fill();

      ctx.strokeStyle = "#ffffff";

      ctx.lineWidth =
        config.strokeWidth *
        scale *
        (1 + tension * 0.16);

      ctx.lineJoin = "round";
      ctx.stroke();
    }

    // ========================================
    // ANIMACIÓN ORIGINAL
    // ========================================

    function animate(now) {
      const dt = lastFrame
        ? Math.min(
            (now - lastFrame) / 1000,
            0.05
          )
        : 1 / 60;

      lastFrame = now;

      const audio = readAudio();

      updateSpikes(dt, audio, now);
      draw();

      requestAnimationFrame(animate);
    }

    requestAnimationFrame(animate);

    console.log(
      "UXS Alien Visualizer V4.2 Ready"
    );
  }

  if (document.readyState === "loading") {
    document.addEventListener(
      "DOMContentLoaded",
      initialize
    );
  } else {
    initialize();
  }
})();
